import {Component, computed, DestroyRef, effect, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {Button} from 'primeng/button';
import {TaAppStore} from '../../../state/clcta-store';
import {TEMPLATE_STEPS, TemplateDatasource, TemplateStepCode} from '../../../datasources/template.datasource';
import {CreateTaStepType} from '../../../util/app.types';
import {ErrorMessageService} from '../../../services/error-message.service';
import {CreateTemplateTimeline} from '../../components/create-template/create-template-timeline/create-template-timeline';
import {CreateTemplateTravellers} from '../../components/create-template/create-template-travellers/create-template-travellers';
import {CreateTemplateDetails} from '../../components/create-template/create-template-details/create-template-details';
import {CreateTemplateItinerary} from '../../components/create-template/create-template-itinerary/create-template-itinerary';
import {CreateTemplateReview} from '../../components/create-template/create-template-review/create-template-review';

/** The template wizard: /template/:id?step=travellers|details|itinerary|review. */
@Component({
  selector: 'clc-template-page',
  imports: [
    Button,
    CreateTemplateTimeline,
    CreateTemplateTravellers,
    CreateTemplateDetails,
    CreateTemplateItinerary,
    CreateTemplateReview,
  ],
  templateUrl: './clc-template-page.html',
})
export class ClcTemplatePage implements OnInit {
  store = inject(TaAppStore);
  templateDatasource = inject(TemplateDatasource);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private errorMessageService = inject(ErrorMessageService);

  templateId = signal<string>('');
  currentStep = signal<TemplateStepCode>(CreateTaStepType.TRAVELLERS);
  loaded = computed<boolean>(() => !!this.templateId() && this.templateDatasource.template()?.id === this.templateId());

  currentIndex = computed<number>(() => TEMPLATE_STEPS.findIndex(step => step.code === this.currentStep()));
  currentSubtitle = computed<string>(() => TEMPLATE_STEPS[this.currentIndex()].subtitle);
  canContinue = computed<boolean>(() => this.templateDatasource.stepValid()[this.currentStep()]);

  protected readonly CreateTaStepType = CreateTaStepType;

  constructor() {
    effect(() => {
      const name = this.templateDatasource.template()?.name;
      if (this.loaded()) {
        this.store.updateBreadCrumbData({
          title: 'Edit Template - ' + (name ?? ''),
          titleIcon: 'fa-regular fa-location-plus fa-lg',
          showSaveStatus: true,
          templateMode: true,
        });
      }
    });
  }

  ngOnInit(): void {
    this.store.updateBreadCrumbData({title: 'Edit Template', titleIcon: 'fa-regular fa-location-plus fa-lg', templateMode: true});

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params.get('id') ?? '';
      this.templateId.set(id);
      // Forced: a copy cached by the home page's summary panel may be stale.
      this.templateDatasource.loadTemplate(id, true).then(ok => {
        if (!ok && this.templateId() === id) {
          this.errorMessageService.showTemporaryError('This template could not be loaded.', 5000);
          this.router.navigate(['/home'], {queryParams: {tab: 'templates'}});
        }
      });
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const step = TEMPLATE_STEPS.find(s => s.code === params.get('step'))?.code;
      this.currentStep.set(step ?? CreateTaStepType.TRAVELLERS);
    });
  }

  setStep(step: TemplateStepCode): void {
    this.router.navigate([], {relativeTo: this.route, queryParams: {step}});
  }

  goBack(): void {
    this.setStep(TEMPLATE_STEPS[Math.max(0, this.currentIndex() - 1)].code);
  }

  saveAndContinue(): void {
    this.setStep(TEMPLATE_STEPS[Math.min(TEMPLATE_STEPS.length - 1, this.currentIndex() + 1)].code);
  }
}
